// src/pages/OnboardingMentorPage.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Logo from '../components/Logo';
import PrivacyAccordion from '../components/PrivacyAccordion';
import PasswordField from '../components/forms/PasswordField';

import { authApi, mentoringApi } from '../axiosInstance';

/* ===========================
   Utils
   =========================== */

// Fehler/Objekte sicher in Text verwandeln
function toText(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object') return val.error || val.message || JSON.stringify(val);
  try { return String(val); } catch { return ''; }
}

// SHA-256 Hash für Consent-Texte (Protokollierung)
async function hashConsentText(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// Cookie-Fallback
function readCookie(name) {
  const hit = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.split('=')[1]) : '';
}

/* ===========================
   Component
   =========================== */

export default function OnboardingMentorPage({ csrfToken }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [backendContent, setBackendContent] = useState('');
  const [error, setError] = useState('');

  // ✅ Skip Password, wenn User aus Login kommt (Profil unvollständig, aber Passwort existiert)
  const [skipPassword, setSkipPassword] = useState(() => {
    try {
      return sessionStorage.getItem('ONBOARDING_SKIP_PASSWORD') === '1';
    } catch {
      return false;
    }
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');

  const [newPassword, setNewPassword]             = useState('');
  const [confirmPassword, setConfirmPassword]     = useState('');

  // Live-Policy/-Validität (vom PasswordField)
  const [passwordPolicy, setPasswordPolicy]       = useState(null);
  // WICHTIG: undefined = PasswordField hat noch nicht gemeldet → Fallback verwenden
  const [passwordValid, setPasswordValid]         = useState(undefined);
  const [passwordIssues, setPasswordIssues]       = useState([]);

  const [contractConsent, setContractConsent]     = useState(false);
  const [analysisConsent, setAnalysisConsent]     = useState(false);

  const [submitting, setSubmitting]               = useState(false);
  const [showPrivacyModal, setShowPrivacyModal]   = useState(false);

  const clearSkipPasswordFlag = () => {
    try { sessionStorage.removeItem('ONBOARDING_SKIP_PASSWORD'); } catch {}
  };

  // ✅ Skip-Flag aus sessionStorage beim Mount synchronisieren
  useEffect(() => {
    try {
      setSkipPassword(sessionStorage.getItem('ONBOARDING_SKIP_PASSWORD') === '1');
    } catch {
      setSkipPassword(false);
    }
  }, []);

  // HashRouter-kompatibel "?lang=" aus location.hash lesen
  const getLangFromHash = () => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash) return null;
    const qs = hash.split('?')[1] || '';
    const p = new URLSearchParams(qs);
    return p.get('lang');
  };

  /* ===========================
     Sprache / Header
     =========================== */

  // Sprache aus URL / localStorage initialisieren
  useEffect(() => {
    let urlLang = searchParams.get('lang') || getLangFromHash();
    const saved = localStorage.getItem('language') || localStorage.getItem('preferred_lang');

    const norm = (v) => (v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null);
    const desired = norm(urlLang) || norm(saved);

    if (desired === 'de' || desired === 'en') {
      if (i18n.language !== desired) i18n.changeLanguage(desired);
      try {
        localStorage.setItem('language', desired);
        localStorage.setItem('preferred_lang', desired);
      } catch {}
    }
  }, [searchParams, i18n]);

  // wenn Sprache im i18n wechselt, immer im Header mitsenden
  useEffect(() => {
    authApi.defaults.headers.common['Accept-Language'] = i18n.language;
    mentoringApi.defaults.headers.common['Accept-Language'] = i18n.language;
    try {
      localStorage.setItem('language', i18n.language);
      localStorage.setItem('preferred_lang', i18n.language);
    } catch {}
  }, [i18n.language]);

  /* ===========================
     Intro-Text (Backend-HTML)
     =========================== */
  useEffect(() => {
    let mounted = true;
    mentoringApi
      .get(`/mentoring/onboarding-mentor?lang=${encodeURIComponent(i18n.language)}`, {
        withCredentials: true,
        headers: { 'Accept-Language': i18n.language, 'X-Requested-With': 'XMLHttpRequest' },
      })
      .then((res) => {
        if (!mounted) return;
        setBackendContent(typeof res.data === 'string' ? res.data : '');
      })
      .catch(() => {
        if (!mounted) return;
        // Kein Hard-Fail – i18n-Fallback im Render
        setBackendContent('');
      });
    return () => { mounted = false; };
  }, [i18n.language]);

  /* ===========================
     Policy laden (mit Fallback)
     =========================== */
  useEffect(() => {
    if (skipPassword) {
      setPasswordPolicy(null);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const r = await authApi.get('/auth/password/policy', { withCredentials: true });
        if (mounted) setPasswordPolicy(r.data || null);
      } catch {
        try {
          const r2 = await authApi.get('/auth/password-policy', { withCredentials: true });
          if (mounted) setPasswordPolicy(r2.data || null);
        } catch {
          if (mounted) setPasswordPolicy(null); // PasswordField hat Defaults
        }
      }
    })();
    return () => { mounted = false; };
  }, [skipPassword]);

  /* ===========================
     Consent-Texte für Protokoll
     =========================== */
  const contractConsentText = t('onboardingMentor.contractConsentLong');
  const analysisConsentText = t('onboardingMentor.analysisConsentLong');
  const consentVersion = 'v1.0';

  const openPrivacyModal  = (e) => { e.preventDefault(); setShowPrivacyModal(true); };
  const closePrivacyModal = () => setShowPrivacyModal(false);

  const passwordsMatch = skipPassword
    ? true
    : newPassword.length > 0 && confirmPassword.length > 0 && newPassword === confirmPassword;

  /* ===========================
     CSRF laden (Route + Fallback)
     =========================== */
  async function getCsrf() {
    try {
      const r = await authApi.get('/csrf-token', { withCredentials: true });
      return r?.data?.csrfToken || '';
    } catch {
      return (
        csrfToken ||
        readCookie('_csrf') ||
        readCookie('XSRF-TOKEN') ||
        readCookie('CSRF-TOKEN') ||
        readCookie('csrfToken') ||
        ''
      );
    }
  }

  /* ===========================
     Submit
     =========================== */
  async function handleFinishOnboarding(e) {
    e.preventDefault();
    setError('');

    // Robuste Validierung: entweder echtes OK vom PasswordField ODER Fallback (>=12 Zeichen)
    const passValidEffective = skipPassword
      ? true
      : passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

    if (!passValidEffective) {
      setError(t('onboardingMentor.passwordInvalid', 'Bitte ein sicheres Passwort wählen.'));
      return;
    }
    if (!passwordsMatch) {
      setError(t('onboardingMentor.passwordsNoMatch'));
      return;
    }
    if (!contractConsent || !analysisConsent) {
      setError(t('onboardingMentor.consentRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const csrf = await getCsrf();

      // 1) Profil speichern (Mentoring-API)
      try {
        await mentoringApi.patch(
          '/mentoring/onboarding-mentor/profile',
          { firstName, lastName },
          {
            withCredentials: true,
            headers: {
              'X-CSRF-Token': csrf,
              'Accept-Language': i18n.language,
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );
      } catch (e) {
        const msg = e?.response?.data;
        if (
          e?.response?.status === 400 &&
          (msg === 'Onboarding schon abgeschlossen' || msg?.error === 'Onboarding schon abgeschlossen')
        ) {
          // bewusst kein return — Consent-Calls müssen trotzdem ausgeführt werden
        } else {
          throw e;
        }
      }

      // 2) Passwort setzen – nur wenn nicht skip
      if (!skipPassword) {
        await mentoringApi.post(
          '/mentoring/onboarding-mentor/password',
          { newPassword },
          {
            withCredentials: true,
            headers: {
              'X-CSRF-Token': csrf,
              'Accept-Language': i18n.language,
              'X-Requested-With': 'XMLHttpRequest',
            },
          }
        );
      }

      // 3) Zwei Consents (Mentoring-API)
      const [contractHash, analysisHash] = await Promise.all([
        hashConsentText(contractConsentText),
        hashConsentText(analysisConsentText),
      ]);

      await mentoringApi.post(
        '/mentoring/consent',
        {
          consent_type: 'contract_participation',
          consent_status: 'granted',
          consent_version: consentVersion,
          consent_text_hash: contractHash,
          language: i18n.language,
        },
        { withCredentials: true, headers: { 'X-CSRF-Token': csrf } }
      );

      await mentoringApi.post(
        '/mentoring/consent',
        {
          consent_type: 'mentor_match_analysis',
          consent_status: 'granted',
          consent_version: consentVersion,
          consent_text_hash: analysisHash,
          language: i18n.language,
        },
        { withCredentials: true, headers: { 'X-CSRF-Token': csrf } }
      );

      // 4) Onboarding als abgeschlossen markieren
      try {
        await mentoringApi.post('/mentoring/onboarding/complete');
      } catch (err) {
        console.error('onboarding/complete failed:', err);
      }

      // 5) Weiter
      clearSkipPasswordFlag();
      navigate(`/mentor-profile?lang=${i18n.language}`);
    } catch (err) {
      const payload = err?.response?.data;
      setError(
        typeof payload === 'string'
          ? payload
          : toText(payload) || t('onboardingMentor.loadingError')
      );
    } finally {
      setSubmitting(false);
    }
  }

  /* ===========================
     Render
     =========================== */

  // Effektive Schaltflächen-Enable-Logik (mit Fallback, falls PasswordField noch nicht gemeldet hat)
  const passValidEffective = skipPassword
    ? true
    : passwordValid === true || (passwordValid === undefined && newPassword.length >= 12);

  const canSubmit =
    contractConsent && analysisConsent && passwordsMatch && !submitting && passValidEffective;

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white">
      {/* Logo */}
      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      {/* Titel */}
      <h2 className="text-3xl md:text-4xl font-bold mb-8">
        {t('onboardingMentor.heading')}
      </h2>

      {/* Einleitung (Backend-HTML bevorzugt; sonst i18n-Fallback) */}
      <div className="bg-gray-800 p-6 rounded-2xl shadow-md mb-8 w-full max-w-2xl text-white">
        {backendContent ? (
          <div dangerouslySetInnerHTML={{ __html: backendContent }} />
        ) : (
          <p>{t('onboardingMentor.intro')}</p>
        )}
      </div>

      {/* Formular */}
      <form
        onSubmit={handleFinishOnboarding}
        className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md"
      >
        {/* Vorname */}
        <div className="mb-6">
          <label htmlFor="firstName" className="block mb-2 font-semibold">
            {t('onboardingMentor.firstName')}
          </label>
          <input
            id="firstName"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="given-name"
          />
        </div>

        {/* Nachname */}
        <div className="mb-6">
          <label htmlFor="lastName" className="block mb-2 font-semibold">
            {t('onboardingMentor.lastName')}
          </label>
          <input
            id="lastName"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="family-name"
          />
        </div>

        {/* Passwort-Bereich nur wenn NICHT skip */}
        {!skipPassword && (
          <>
            {/* Passwort (mit Policy + Live-Feedback) */}
            <div className="mb-6">
              <label htmlFor="newPassword" className="block mb-2 font-semibold">
                {t('onboardingMentor.password')}
              </label>

              <PasswordField
                value={newPassword}
                onChange={setNewPassword}
                policy={passwordPolicy}
                onValidityChange={(ok, issues) => {
                  // ok kann true/false sein – wenn das Feld noch nicht entschieden hat, lassen wir undefined stehen
                  setPasswordValid(ok === true ? true : ok === false ? false : undefined);
                  setPasswordIssues(Array.isArray(issues) ? issues : []);
                }}
              />

              {passwordIssues.length > 0 && (
                <ul className="mt-2 text-xs text-red-400 list-disc ml-5">
                  {passwordIssues.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Passwort bestätigen */}
            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block mb-2 font-semibold">
                {t('onboardingMentor.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full p-2 bg-gray-700 rounded text-white"
                autoComplete="new-password"
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <div className="text-xs text-red-400 mt-1">
                  {t('onboardingMentor.passwordsNoMatch')}
                </div>
              )}
            </div>
          </>
        )}

        {/* Consent 1 (mit Trans + Popup-Link) */}
        <div className="mb-4">
          <label
            htmlFor="contractConsent"
            className="flex items-start space-x-3 cursor-pointer select-none"
          >
            <input
              id="contractConsent"
              type="checkbox"
              checked={contractConsent}
              onChange={(e) => setContractConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>
              <Trans
                i18nKey="onboardingMentor.contractConsent"
                components={[
                  // eslint-disable-next-line
                  <button
                    onClick={(ev) => {
                      ev.preventDefault();
                      openPrivacyModal(ev);
                    }}
                    type="button"
                    className="underline text-mentixxgreen hover:text-green-300 focus:outline-none"
                    tabIndex={0}
                  />,
                ]}
              />
            </span>
          </label>
          <div className="ml-6 mt-1 text-xs text-gray-300">
            {t('onboardingMentor.contractConsentHint')}
          </div>
        </div>

        {/* Consent 2 */}
        <div className="mb-4">
          <label
            htmlFor="analysisConsent"
            className="flex items-start space-x-3 cursor-pointer select-none"
          >
            <input
              id="analysisConsent"
              type="checkbox"
              checked={analysisConsent}
              onChange={(e) => setAnalysisConsent(e.target.checked)}
              className="mr-2 mt-1"
              required
            />
            <span>{t('onboardingMentor.analysisConsent')}</span>
          </label>
          <div className="ml-6 mt-1 text-xs text-gray-300">
            {t('onboardingMentor.analysisConsentHint')}
          </div>
        </div>

        <button
          type="submit"
          className={`w-full bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 rounded-2xl transition mt-2 ${
            !canSubmit ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          disabled={!canSubmit}
        >
          {submitting ? t('onboardingMentor.submitting') : t('onboardingMentor.finish')}
        </button>
      </form>

      {/* Datenschutz-Popup */}
      {showPrivacyModal && (
        <PrivacyAccordion
          userRole="mentors"
          language={i18n.language}
          onClose={closePrivacyModal}
        />
      )}
    </div>
  );
}
